import { Capacitor } from '@capacitor/core';
import ReactGA from 'react-ga4';

import { APP_VERSION, IS_RUNNING_TESTS } from '../../common/constants';
import { isGoogleAnalyticsTrackingEnabled } from './tracking-privacy';

/**
 * Common parameters included with every GA4 event.
 */
function getCommonParams() {
  return {
    platform: Capacitor.getPlatform(), // 'ios' | 'android' | 'web'
    app_version: APP_VERSION,
  };
}

/**
 * Send a GA4 custom event with common parameters automatically attached.
 */
function trackEvent(eventName: string, params: Record<string, string | number | boolean | undefined> = {}) {
  if (IS_RUNNING_TESTS || !isGoogleAnalyticsTrackingEnabled()) {
    return;
  }

  ReactGA.event(eventName, {
    ...getCommonParams(),
    ...params,
  });
}

// ---------------------------------------------------------------------------
// 1. Onboarding EVSE
// ---------------------------------------------------------------------------

export type PermissionOutcome = 'granted' | 'denied';

// User registered an SE account.
// NOTE: We are not able to track this event from the app. This should be tracked from the backend.
export function trackUserRegistered() {
  trackEvent('user_registered');
}

// Location permission outcome.
export function trackLocationPermissionOutcome(outcome: PermissionOutcome) {
  trackEvent('location_permission_result', {
    permission_type: 'location',
    permission_outcome: outcome,
  });
}

// Camera access prompt outcome.
export function trackCameraAccessOutcome(outcome: PermissionOutcome) {
  trackEvent('camera_access_result', {
    permission_type: 'camera',
    permission_outcome: outcome,
  });
}

// Connected to charger WiFi for network setup.
export function trackConnectedToChargerWifi() {
  trackEvent('connected_to_charger_wifi');
}

// Received list of networks from EVSE.
export function trackReceivedNetworkList(networkCount: number) {
  trackEvent('received_network_list', {
    network_count: networkCount,
  });
}

// User offline, no internet access, can't register EVSE.
export function trackUserOfflineSetupError() {
  trackEvent('user_offline_setup_error');
}

// Charger added successfully.
export function trackChargerAddedSuccessfully() {
  trackEvent('charger_added_successfully');
}

// ---------------------------------------------------------------------------
// 2. Tariffs Setup
// ---------------------------------------------------------------------------

// User skipped tariff setup.
// TODO: Use this event when user skips tariff setup from the new charger onboarding flow. Currently, tariff setup is not in the onboarding flow, so this event is not being used.
export function trackTariffSkipped() {
  trackEvent('tariff_skipped');
}

// User imported tariff from WattBuy.
export function trackTariffImportedFromWattbuy() {
  trackEvent('tariff_set', {
    action: 'save',
    tariff_source: 'wattbuy',
  });
}

// User manually set tariff.
export function trackTariffSetManually() {
  trackEvent('tariff_set', {
    action: 'save',
    tariff_source: 'manual',
  });
}

// ---------------------------------------------------------------------------
// 3. Schedule Setup
// ---------------------------------------------------------------------------

// User skipped schedule setup.
export function trackScheduleSkipped() {
  trackEvent('schedule_skipped');
}

// User created a schedule.
export function trackScheduleCreated() {
  trackEvent('schedule_created');
}

// User encountered error in schedule config.
export function trackScheduleConfigError(errorMessage: string) {
  trackEvent('schedule_config_error', {
    error_message: errorMessage,
  });
}

// ---------------------------------------------------------------------------
// 4. Charger Control
// ---------------------------------------------------------------------------

// User stopped charging.
export function trackChargingStopped() {
  trackEvent('charging_stopped');
}

// User force-started charging.
export function trackChargingForceStarted() {
  trackEvent('charging_force_started');
}

// User plugged out cable.
export function trackCablePluggedOut() {
  trackEvent('cable_plugged_out');
}

// User plugged in cable without schedule defined.
export function trackCablePluggedInWithoutSchedule() {
  trackEvent('cable_plugged_in_no_schedule');
}

// ---------------------------------------------------------------------------
// 5. Historical Data
// ---------------------------------------------------------------------------

// User opened charging activity.
export function trackActivityOpened() {
  trackEvent('activity_opened');
}

// User changed timeframe in activity view.
export function trackActivityTimeframeChanged(timeframe: string) {
  trackEvent('activity_timeframe_changed', {
    timeframe,
  });
}

// User navigated in history (changed week/date/month).
export function trackActivityDateNavigated(direction: 'forward' | 'back', dateRangeType: string) {
  trackEvent('activity_date_navigated', {
    direction,
    date_range_type: dateRangeType,
  });
}

// ---------------------------------------------------------------------------
// 6. Hamburger Menu
// ---------------------------------------------------------------------------

// User opened hamburger menu.
export function trackHamburgerMenuOpened() {
  trackEvent('hamburger_menu_opened');
}

// User opened charger info page.
export function trackChargerInfoOpened() {
  trackEvent('charger_info_opened');
}

// User opened WiFi config page.
export function trackWifiConfigOpened() {
  trackEvent('wifi_config_opened');
}

// User started process to change WiFi network.
export function trackWifiChangeStarted() {
  trackEvent('wifi_change_started');
}

// User removed guest share.
export function trackGuestShareRemoved() {
  trackEvent('guest_share_removed');
}

// User added guest user to share charger.
export function trackGuestShareAdded() {
  trackEvent('guest_share_added');
}

// User opened schedule settings.
export function trackScheduleSettingsOpened() {
  trackEvent('schedule_settings_opened');
}

// User opened energy rates.
export function trackEnergyRatesOpened() {
  trackEvent('energy_rates_opened');
}

// User opened display config.
export function trackDisplayConfigOpened() {
  trackEvent('display_config_opened');
}

// UI element type test: We want to see how many users are clicking on the SmartAmp status pill, so we can decide whether to change the design in the future.
export function trackPillClick(label: string) {
  trackEvent('pill_element_clicked', {
    label,
  });
}
